// frontend/src/components/Shared/DuplicadosCandidatos.js
// Detección y fusión de candidatos duplicados (mismo correo dentro de la
// misma empresa, registrados por error en dos filas distintas). Inspirado
// en la función de combinar candidatos de TeamTailor: se elige cuál de los
// perfiles queda como principal — por default el de actividad más
// reciente— y todo el historial de pruebas de los demás se une ahí, sin
// perder ningún resultado. Se usa tanto desde el Dashboard de admin (ve
// todas las empresas) como desde el Portal de Empresa (solo la suya); el
// endpoint ya resuelve el alcance según el rol de quien hace la llamada.

import React, { useState, useEffect, useCallback } from 'react';
import { Copy, X, Mail, FileText, Link2, PartyPopper } from 'lucide-react';
import './DuplicadosCandidatos.css';

const claveGrupo = (g) => `${g.empresa_id}:${g.email.toLowerCase().trim()}`;

const DuplicadosCandidatos = ({ onClose, onCombinado, mostrarEmpresa = false }) => {
  const [grupos, setGrupos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [combinando, setCombinando] = useState(null);
  const [seleccion, setSeleccion] = useState({});

  const cargar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/candidatos/duplicados', { credentials: 'include' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'No se pudieron cargar los duplicados');
      const lista = Array.isArray(data) ? data : [];
      setGrupos(lista);

      // Preselecciona como principal al candidato con la actividad más
      // reciente (o el más nuevo si ninguno tiene pruebas todavía) — el
      // admin puede cambiarlo antes de confirmar.
      setSeleccion(prev => {
        const siguiente = { ...prev };
        lista.forEach(g => {
          const clave = claveGrupo(g);
          if (siguiente[clave]) return;
          const mejor = [...g.candidatos].sort((a, b) => {
            const fa = new Date(a.ultima_actividad || a.created_at).getTime();
            const fb = new Date(b.ultima_actividad || b.created_at).getTime();
            return fb - fa;
          })[0];
          siguiente[clave] = mejor?.id;
        });
        return siguiente;
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const combinar = async (grupo) => {
    const clave = claveGrupo(grupo);
    const principalId = seleccion[clave];
    const secundarios = grupo.candidatos.filter(c => c.id !== principalId);
    if (!principalId || secundarios.length === 0) return;

    const nombres = secundarios.map(c => c.nombre_completo).join(', ');
    const confirmado = window.confirm(
      `¿Combinar a "${nombres}" dentro del candidato elegido?\n\nTodo su historial de pruebas se unirá en un solo perfil. Esta acción no se puede deshacer.`
    );
    if (!confirmado) return;

    setCombinando(clave);
    try {
      for (const secundario of secundarios) {
        const res = await fetch('/api/candidatos/combinar', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ candidato_id_principal: principalId, candidato_id_secundario: secundario.id })
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'No se pudo combinar');
      }
      setGrupos(prev => prev.filter(g => claveGrupo(g) !== clave));
      if (onCombinado) onCombinado();
    } catch (err) {
      alert(err.message);
      await cargar();
    } finally {
      setCombinando(null);
    }
  };

  return (
    <div className="dup-overlay">
      <div className="dup-modal">
        <div className="dup-header">
          <h2><Copy size={18} /> Posibles candidatos duplicados</h2>
          <button type="button" className="dup-close" onClick={onClose} aria-label="Cerrar"><X size={18} /></button>
        </div>
        <div className="dup-body">
          <p className="dup-intro">
            Se detectan cuando dos candidatos comparten el mismo correo{mostrarEmpresa ? '' : ' dentro de la misma empresa'},
            pero quedaron registrados por separado (por ejemplo, con un CURP distinto por error de captura).
            Elige qué perfil quieres conservar como principal — el historial de pruebas de ambos se une ahí, no se pierde nada.
          </p>

          {loading ? (
            <p className="dup-empty">Buscando duplicados...</p>
          ) : error ? (
            <p className="dup-empty dup-error">{error}</p>
          ) : grupos.length === 0 ? (
            <p className="dup-empty"><PartyPopper size={20} /> No se encontraron candidatos duplicados.</p>
          ) : (
            grupos.map(grupo => {
              const clave = claveGrupo(grupo);
              return (
                <div key={clave} className="dup-grupo">
                  <div className="dup-grupo-header">
                    <span className="dup-grupo-email"><Mail size={14} /> {grupo.email}</span>
                    {mostrarEmpresa && <span className="dup-grupo-empresa">{grupo.empresa_nombre}</span>}
                  </div>
                  <div className="dup-candidatos-row">
                    {grupo.candidatos.map(c => (
                      <label
                        key={c.id}
                        className={`dup-candidato-card ${seleccion[clave] === c.id ? 'dup-candidato-elegido' : ''}`}
                      >
                        <input
                          type="radio"
                          name={`principal-${clave}`}
                          checked={seleccion[clave] === c.id}
                          onChange={() => setSeleccion(prev => ({ ...prev, [clave]: c.id }))}
                        />
                        <div className="dup-candidato-info">
                          <strong>{c.nombre_completo}</strong>
                          <small>CURP: {c.curp}</small>
                          <small>Registrado: {new Date(c.created_at).toLocaleDateString('es-MX')}</small>
                          <span className="dup-candidato-badge">
                            <FileText size={12} /> {c.total_asignaciones} prueba{c.total_asignaciones !== 1 ? 's' : ''}
                            {c.ultima_actividad ? ` · última actividad ${new Date(c.ultima_actividad).toLocaleDateString('es-MX')}` : ''}
                          </span>
                          {seleccion[clave] === c.id && <span className="dup-tag-principal">Se conservará como principal</span>}
                        </div>
                      </label>
                    ))}
                  </div>
                  <button
                    type="button"
                    className="dup-btn-combinar"
                    onClick={() => combinar(grupo)}
                    disabled={combinando === clave}
                  >
                    {combinando === clave ? 'Combinando...' : <><Link2 size={14} /> Combinar en el elegido</>}
                  </button>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default DuplicadosCandidatos;
